import { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { createPost, editPost } from '../../store/slices/post.slice'
import { postService } from '../../services/PostSystem'
import { cloudinaryService } from '../../services/cloudinary.service'
import toast from 'react-hot-toast'
import { LexicalTextEditor } from '../../cmps/TextEditor/LexicalTextEditor'
import { Upload, X } from 'lucide-react'

export function PostForm({ post, onSave = () => {}, onCancel = () => {} }) {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const loggedInUser = useSelector((state) => state.userModule.loggedInUser)

  const [formState, setFormState] = useState(postService.getEmptyPost())
  const [selectedImage, setSelectedImage] = useState(null)
  const [isUploading, setIsUploading] = useState(false)
  const [descriptionLength, setDescriptionLength] = useState(0)
  const [contentLength, setContentLength] = useState(0)
  const [editorRef, setEditorRef] = useState(null)

  const tagOptions = [
    'חוות דעת',
    'חדשות',
    'סיקור',
    'השוואה',
    'ספויילר',
    'המלצות אנימה',
    'המלצות מנגה',
    ...(loggedInUser?.role === 'admin' ? ['הכרזה'] : []),
  ]
  useEffect(() => {
    if (post) {
      setFormState({ ...postService.getEmptyPost(), ...post })
      setDescriptionLength(post.description?.length || 0)
      const plain =
        new DOMParser().parseFromString(post.content || '', 'text/html').body
          .textContent || ''
      setContentLength(plain.length)
    }
  }, [post])

  function handleChange({ target }) {
    const { name, value, type, checked } = target

    let newValue = type === 'checkbox' ? checked : value

    if (name === 'linkedAnime') {
      try {
        const match = value.match(/\/anime\/(\d+\/[\w\d\-_]+)/)
        if (match) newValue = match[1] // כלומר רק '58514/The_Apothecary_Diaries_Season_2'
      } catch (err) {
        console.warn('שגיאה בפיענוח קישור לאנימה:', err)
      }
    }

    setFormState((prev) => ({
      ...prev,
      [name]: newValue,
    }))

    if (name === 'description') setDescriptionLength(newValue.length)
  }

  function handleTagSelection(tag) {
    try {
      setFormState((prev) => {
        const alreadySelected = prev.tags.includes(tag)

        // הסרת תגית קיימת
        if (alreadySelected) {
          return { ...prev, tags: prev.tags.filter((t) => t !== tag) }
        }

        // הגבלת בחירה לשתי תגיות
        if (prev.tags.length >= 2) {
          toast.error('ניתן לבחור עד שתי תגיות בלבד')
          return prev
        }

        return { ...prev, tags: [...prev.tags, tag] }
      })
    } catch (err) {
      console.error('שגיאה בבחירת תגית:', err)
      toast.error('שגיאה בעת בחירת תגית')
    }
  }

  function handleEditorChange(htmlContent, plainText) {
    setFormState((prev) => ({ ...prev, content: htmlContent }))
    setContentLength(plainText.length)
  }
  async function uploadImageToBackend(file) {
    try {
      setIsUploading(true)
      const { imgUrl } = await cloudinaryService.uploadImage(file, {
        type: 'post-img',
        relatedId: loggedInUser?._id,
      })
      return imgUrl
    } catch (err) {
      toast.error('שגיאה בהעלאת התמונה')
      return null
    } finally {
      setIsUploading(false)
    }
  }

  function validateForm() {
    const { imageUrl, title, description, tags, content } = formState

    if (!imageUrl && !selectedImage) {
      toast.error('חובה תמונה ראשית!')
      return false
    }

    if (!title.trim()) {
      toast.error('חובה כותרת!')
      return false
    }

    if (description.trim().length < 30) {
      toast.error('תיאור קצר מדי!')
      return false
    }

    if (!tags.length) {
      toast.error('בחר תגית אחת לפחות!')
      return false
    }

    if (content.trim().length < 500) {
      toast.error('התוכן קצר מדי!')
      return false
    }

    return true
  }

  async function handleSubmit(ev) {
    ev.preventDefault()

    if (!loggedInUser) {
      toast.error('יש להתחבר!')
      return
    }

    if (!validateForm()) return

    // שליפת התוכן מהעורך
    const htmlContent = editorRef?.getHTML() || ''
    const textContent = editorRef?.getText() || ''

    if (textContent.trim().length < 500) {
      toast.error('התוכן קצר מדי!')
      return
    }

    try {
      // העלאת תמונה (אם יש חדשה)
      let imageUrl = formState.imageUrl
      if (selectedImage) {
        const uploadedUrl = await uploadImageToBackend(selectedImage)
        if (!uploadedUrl) {
          toast.error('העלאת התמונה נכשלה')
          return
        }
        imageUrl = uploadedUrl
      }

      // בניית אובייקט לשליחה
      const postData = {
        ...formState,
        content: htmlContent,
        imageUrl,
        author: { userId: loggedInUser._id },
        createdAt: post?.createdAt || new Date().toISOString(),
      }

      // יצירה או עריכה
      const action = post?._id
        ? editPost({ postId: post._id, updatedData: postData })
        : createPost(postData)

      const saved = await toast.promise(dispatch(action).unwrap(), {
        loading: post ? 'שומר שינויים...' : 'מפרסם את הפוסט...',
        success: post ? 'הפוסט עודכן בהצלחה!' : 'הפוסט פורסם!',
        error: 'שגיאה בשמירה. נסה שוב',
      })

      onSave(saved)
      if (!post) navigate(`/post/${saved._id}`)
    } catch (err) {
      console.error('שגיאה בטופס הפוסט:', err)
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="container mx-auto px-4 py-8 bg-zinc-900 rounded-lg shadow-lg space-y-8 max-w-4xl"
    >
      <h2 className="text-3xl font-extrabold text-center text-white">
        📝 כתיבת פוסט חדש
      </h2>

      {/* Image Upload */}
      <section className="flex flex-col items-center gap-4">
        <label
          htmlFor="post-image"
          className="relative flex items-center justify-center w-[730px] h-[380px] border-2 border-dashed border-zinc-600 rounded-lg cursor-pointer hover:border-primary transition-colors overflow-hidden"
        >
          {selectedImage ? (
            <img
              src={URL.createObjectURL(selectedImage)}
              alt="Preview"
              className="object-cover w-full h-full"
            />
          ) : formState.imageUrl ? (
            <img
              src={formState.imageUrl}
              alt="Preview"
              className="object-cover w-full h-full"
            />
          ) : (
            <div className="flex flex-col items-center gap-2 text-zinc-500">
              <Upload size={50} />
              <span className="text-sm">גרור או לחץ להעלאת תמונה</span>
              <span className="text-sm text-primary">גודל רצוי: 730x380</span>
            </div>
          )}

          <input
            id="post-image"
            type="file"
            accept="image/*"
            onChange={(e) => setSelectedImage(e.target.files[0])}
            className="sr-only"
          />

          {(selectedImage || formState.imageUrl) && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault()
                setSelectedImage(null)
                setFormState((prev) => ({ ...prev, imageUrl: '' }))
              }}
              className="absolute top-2 left-2 bg-gray-600 hover:bg-gray-700 text-white p-1 rounded-full"
              title="הסר תמונה"
            >
              <X size={18} />
            </button>
          )}
        </label>

        {isUploading && (
          <p className="text-sm text-green-400">🔄 מעלה תמונה...</p>
        )}
      </section>

      {/* Title & Linked Anime */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="flex flex-col">
          <label
            htmlFor="title"
            className="mb-2 text-sm font-medium text-gray-200"
          >
            כותרת <span className="text-red-500">*</span>
          </label>
          <input
            id="title"
            type="text"
            name="title"
            value={formState.title}
            onChange={handleChange}
            placeholder="הזן כותרת..."
            className="w-full bg-zinc-800 border border-zinc-700 p-3 rounded-lg focus:border-primary focus:outline-none transition"
          />
        </div>
        <div className="flex flex-col">
          <label
            htmlFor="linkedAnime"
            className="mb-2 text-sm font-medium text-gray-200"
          >
            קישור לאנימה
          </label>
          <input
            id="linkedAnime"
            type="text"
            name="linkedAnime"
            value={formState.linkedAnime}
            onChange={handleChange}
            placeholder="https://..."
            className="w-full bg-zinc-800 border border-zinc-700 p-3 rounded-lg focus:border-primary focus:outline-none transition"
          />
        </div>
      </section>

      {/* Description */}
      <section className="flex flex-col">
        <label
          htmlFor="description"
          className="mb-2 text-sm font-medium text-gray-200"
        >
          תיאור קצר <span className="text-red-500">*</span>
        </label>
        <textarea
          id="description"
          name="description"
          value={formState.description}
          onChange={handleChange}
          maxLength={150}
          rows={3}
          placeholder="כתיבת תיאור קצר (מינימום 30 תווים)..."
          className="w-full bg-zinc-800 border border-zinc-700 p-3 rounded-lg focus:border-primary focus:outline-none transition resize-none"
        />
        <p
          className={`mt-1 text-sm ${
            descriptionLength < 30 ? 'text-red-500' : 'text-green-400'
          }`}
        >
          💬 {descriptionLength} / 150 תווים
        </p>
      </section>

      {/* Tags */}
      <section className="flex flex-col">
        <p className="mb-2 text-sm font-medium text-gray-200">
          בחר תגיות <span className="text-red-500">*</span>
        </p>
        <div className="flex flex-wrap gap-3">
          {tagOptions.map((tag) => (
            <label
              key={tag}
              className={`flex items-center gap-2 px-4 py-2 rounded-full cursor-pointer transition-colors
                ${
                  formState.tags.includes(tag)
                    ? 'bg-primary text-white'
                    : 'bg-zinc-800 text-gray-300 hover:bg-zinc-700'
                }`}
            >
              <input
                type="checkbox"
                checked={formState.tags.includes(tag)}
                onChange={() => handleTagSelection(tag)}
                className="sr-only"
              />
              <span className="text-sm">{tag}</span>
            </label>
          ))}
        </div>
      </section>

      {/* Content Editor */}
      <section className="flex flex-col relative">
        <h3 className="mb-2 text-sm font-medium text-gray-200">
          ✍️ תוכן הכתבה
        </h3>
        <div className=" min-h-[200px]">
          <LexicalTextEditor
            onChange={handleEditorChange}
            initialContent={post?.content || ''}
            shouldLoadDraft={!post?._id}
            editorRefCallback={setEditorRef}
          />
        </div>
        <p
          className={`mt-1 text-sm ${
            contentLength < 500 ? 'text-red-500' : 'text-green-400'
          }`}
        >
          📄 {contentLength} תווים
        </p>
      </section>

      {/* Actions */}
      <section className="flex flex-col sm:flex-row justify-end items-center gap-4">
        <button
          type="button"
          onClick={onCancel}
          className="w-full sm:w-auto bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg transition"
        >
          ביטול
        </button>
        <button
          type="submit"
          className="w-full sm:w-auto bg-primary hover:bg-primary text-white px-6 py-3 rounded-lg transition"
        >
          {post ? 'שמור שינויים' : 'פרסם כתבה'}
        </button>
      </section>
    </form>
  )
}
